import React, { ChangeEvent, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Box, Divider, IconButton, List, ListItem, Pagination, Stack, Tooltip, useTheme } from "@mui/material";
import SanitizedHTML from "components/formatting/SanitizedHTML";
import i18n from "translation";
import { Search } from "@mui/icons-material";
import { Query } from "system/query/Query";
import { DashboardQueryDrilldownDialog } from "components/dialogs/QueryDrilldown/DashboardQueryDrilldownDialog";
import { currentUser } from "system/InfegyUser";
import WidgetAlert, { WidgetErrorType } from "components/widgetParts/WidgetAlert";
import WidgetLayout from "components/widgetParts/WidgetLayout";
import WidgetTitle from "components/widgetParts/WidgetTitle";
import SocialIcon from "components/widgets/utility/SocialIcon";
import { socialInfluencersDefaultSettings } from "./SocialInfluencersUtils";
import withQueryWidget, { WithQueryWidgetProps } from "components/widgetParts/queryWidgetComposition/withQueryWidget";
import { getSingleOriginalRequestByName, getSingleWidgetResponseByName, widgetQueryTitleByResponses } from "system/widget/Widget";

interface Influencer {
  id: number,
  score: number,
  handle?: string,
  display_name: string,
  hits: number,
  positive_hits: number,
  negative_hits: number,
  positivity?: number | null,
  link: string,
  site: string,
  bio: string,
  followers: number,
  following: number,
  country?: string,
  country_flag?: string,
  total_engagements?: number,
  query: object
}

function SocialInfluencersListWidget(props: WithQueryWidgetProps): React.JSX.Element {
  const theme = useTheme();

  const currentState = {...socialInfluencersDefaultSettings, ...props.widget?.data?.settings};

  const apiResponseInfo = getSingleWidgetResponseByName(props.apiResponses, 'social-influencers');
  const originalRequest = getSingleOriginalRequestByName(props.activeWidgetRequests, 'social-influencers');
  const responseBody = apiResponseInfo?.responseBody || {};

  const influencers: Array<Influencer> = responseBody?.influencers ?? [];

  const [drilldownTitle, setDrilldownTitle] = React.useState<string | null>(null);
  const [drilldownQuery, setDrilldownQuery] = React.useState<Query | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const pageSize = currentState.pageSize || socialInfluencersDefaultSettings.pageSize;
  const pageCount = Math.ceil(influencers.length / pageSize);
  const scrollRegion = useRef<HTMLElement>();

  const postStyle = {
    backgroundColor: theme.palette.common.white,
    margin: "0 0 10px 0",
    borderRadius: "10px",
    border: "1px solid rgba(159, 162, 191, .18)",
    boxShadow: "0px 4px 8px rgba(159, 162, 191, .18),0px 2px 2px rgba(159, 162, 191, 0.32)",
    display: "flex",
    flexDirection: "column",
  }

  const influencerRows = useMemo(() => {
    scrollRegion.current?.scrollTo({top: 0, behavior: "smooth"});
    const startIndex = (pageNumber - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const influencersPage = influencers.slice(startIndex, endIndex);
    return influencersPage.map((influencer:any, index:number): any => {
      return (
        <ListItem key={index} sx={postStyle}>
          <Box sx={{ width: "100%" }}>
              {/* Post Title */}
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", width: "100%", alignItems: "center" }}>
                <Box>
                  <Link to={influencer.link} target="_blank" rel="noreferrer" style={{textDecorationLine:"none"}}>
                    <Box sx={{display:"inline-block", verticalAlign:"bottom", marginTop:"2px", marginRight:"8px"}}>
                      <SocialIcon site={influencer.site}/>
                    </Box>
                    <Box sx={{display:"inline-block", color:theme.colors.primary.main, fontSize: "1.0em", fontWeight: "bold", verticalAlign:"top" }}>@{influencer.handle}</Box>
                  </Link>
                </Box>
                <Box sx={{ overflow: "hidden", textAlign: "center" }}>
                  <Link to={influencer.link} target="_blank" rel="noreferrer" style={{textDecorationLine:"none"}}>
                    <Box sx={{textDecoration:"none", fontSize:"1.0em", fontWeight: "bold", color:theme.colors.secondary.main, textDecorationLine:"none"}}>
                      {influencer.display_name}
                    </Box>
                  </Link>
                </Box>
                <Box sx={{fontSize:"1.2em", display: "flex", justifyContent: "flex-end"}}>
                  {currentUser() &&
                    <IconButton
                      size="small"
                      sx={{ marginLeft: "0.25em" }}
                      onClick={(event: any): void => {
                        setDrilldownQuery(new Query({ query: influencer.query }));
                        setDrilldownTitle(influencer.handle ? ('@' + influencer.handle) : influencer.display_name);
                    }}>
                      <Tooltip title={i18n.t('widget-types.social-influencers.labels.drilldown')} followCursor>
                        <Search />
                      </Tooltip>
                    </IconButton>
                  }
                </Box>
              </Box>
              <Divider sx={{ margin: "5px 0" }} />
              <Stack
                direction="row"
                sx={{
                  justifyContent: "space-between",
                  textAlign: "center"
                }}>
                <Box sx={{
                  justifyItems: "center"
                }}>
                  <Box>{i18n.t('widget-types.social-influencers.labels.rank')}</Box>
                  <Box
                    sx={{
                      color: theme.colors.primary.main,
                      fontWeight: "bold"
                    }}>
                    {i18n.t('intlNumber', { val: startIndex + index + 1 })}
                  </Box>
                </Box>
                <Box sx={{
                  justifyItems: "center"
                }}>
                  <Box>{i18n.t('widget-types.social-influencers.labels.records')}</Box>
                  <Box
                    sx={{
                      color: theme.colors.primary.main,
                      fontWeight: "bold"
                    }}>{i18n.t('intlNumber', { val: influencer.hits})}</Box>
                </Box>
                <Box sx={{
                  justifyItems: "center"
                }}>
                  <Box>{i18n.t('widget-types.social-influencers.labels.engagements')}</Box>
                  <Box
                    sx={{
                      color: theme.colors.primary.main,
                      fontWeight: "bold"
                    }}>{i18n.t('intlNumber', { val: influencer.total_engagements})}</Box>
                </Box>
                <Box sx={{
                  justifyItems: "center"
                }}>
                  <Box>{i18n.t('widget-types.social-influencers.labels.followers')}</Box>
                  <Box
                    sx={{
                      color: theme.colors.primary.main,
                      fontWeight: "bold"
                    }}>{i18n.t('intlNumber', { val: influencer.followers})}</Box>
                </Box>
                <Box sx={{
                  justifyItems: "center"
                }}>
                  <Box>{i18n.t('widget-types.social-influencers.labels.following')}</Box>
                  <Box
                    sx={{
                      color: theme.colors.primary.main,
                      fontWeight: "bold"
                    }}>{i18n.t('intlNumber', { val: influencer.following})}</Box>
                </Box>
                <Box sx={{
                  justifyItems: "center"
                }}>
                  <Box>{i18n.t('widget-types.social-influencers.labels.positivity')}</Box>
                  <Box
                    sx={{
                      color: theme.colors.primary.main,
                      fontWeight: "bold"
                    }}>{influencer.positivity ? i18n.t('intlNumber', { val: influencer.positivity*100.0})+'%' : "N/A"}</Box>
                </Box>
              </Stack>
              {/* Post Body */}
              {influencer.bio &&
                <Box
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: theme.general.borderRadius,
                    display: "flex",
                    margin: "10px 0px",
                    padding: "10px",
                    width: "100%",
                    wordBreak: "break-word"
                  }}
                >
                  <Box sx={{ width: "100%" }}>
                    <SanitizedHTML html={influencer.bio} forbidImages={true} />
                  </Box>
                </Box>
              }
          </Box>
        </ListItem>
      );
    });
  }, [influencers, pageNumber])

  return (
    <>
      {influencers.length === 0 &&
        <WidgetAlert type={WidgetErrorType.NO_DATA} />
      }
      {influencers.length !== 0 &&
      <WidgetLayout
        header={
          <WidgetTitle
            alignment={currentState.widgetTitleAlignment}
            customTitle={currentState.customWidgetTitle}
            defaultTitle={i18n.t('widget-types.social-influencers.label')}
            settingsTitle={currentState.widgetTitle}
            widgetQueryTitle={widgetQueryTitleByResponses(props.apiResponses, "social-influencers", i18n)}
          />
        }
        footer={pageCount > 1 &&
          <Box
            sx={{
              flex: "0 1 auto",
              display: "flex",
              justifyContent: "center",
              pt: 1,
              pb: 1,
              borderTop: `1px solid ${theme.palette.divider}`
            }}>
            <Pagination
              count={pageCount}
              onChange={(event: ChangeEvent<unknown>, value: number) => setPageNumber(value)}
              page={pageNumber}
              shape="rounded"
              />
          </Box>
        }
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%"
          }}>
          <Box
            ref={scrollRegion}
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: "1 1 auto",
              overflow: "auto"
            }}>
            <List className="influencers-image-content" sx={{ paddingTop: 0 }}>
              <ListItem>
                <Box sx={{ width: "100%", display: "flex", justifyContent: "space-evenly", flexWrap: "wrap", marginTop:"0px", color:theme.colors.secondary.main }}>
                  <Box>
                    <Box sx={{display:"inline-block", color:theme.colors.primary.main, marginRight:"10px", fontWeight:"bold"}}>
                      {i18n.t('intlNumber', { val: responseBody?.est_total_sources })}
                    </Box>
                    {i18n.t('widget-types.social-influencers.labels.authors')}
                  </Box>
                <Box>
                  <Box sx={{display:"inline-block", color:theme.colors.primary.main, marginRight:"10px", fontWeight:"bold"}}>
                    {i18n.t('intlNumber', { val: currentState.displayValue === "average" ? responseBody?.average_hits_per_source : responseBody?.total_count })}
                  </Box>
                  {currentState.displayValue === "average" ? i18n.t('widget-types.social-influencers.labels.average-records') : i18n.t('widget-types.social-influencers.labels.total-records')}
                </Box>
              </Box>
            </ListItem>
              {influencerRows}
            </List>
          </Box>
          {drilldownQuery && drilldownTitle && props.queryInfo &&
            <DashboardQueryDrilldownDialog
              title={drilldownTitle}
              queryInfo={props.queryInfo}
              onClose={() => setDrilldownQuery(null)}
              activeQueryIds={apiResponseInfo?.originatingRequest?.activeQueryIds}
              widgetRequestInfo={originalRequest}
              drilldownQuery={drilldownQuery}
            />
          }
        </Box>
      </WidgetLayout>
      }
    </>
  );
}

export default withQueryWidget(SocialInfluencersListWidget, {
  hasSettingsDialog: true,
  hasQueryDialog: true
});
